// v0.70 — connect helpers shared by the Drop claim and confirm pages.
//
// Same rules as pay-by-link (payView): viewing needs nothing; acting needs one
// Face ID. A cached credential (m.cred) rebuilds the account silently; only a
// signature — or an explicit "another wallet" pick — prompts.

import type { WalletAdapter } from "../adapters/WalletAdapter";
import { assignName, proposeName, setProfile } from "../api";
import { LOGO_M } from "../logo";
import { clearCredential, readCredential, saveCredential } from "../credentialStore";
import { readFlagIdb, readFlagSync, setFlag } from "../deviceFlag";
import { ARROW_CIRCLE, ARROW_CIRCLE_FILL } from "../icons";
import { holdField } from "./holdField";

/** Does THIS browser already hold a live M wallet? (m.hasWallet is the authority.) */
export async function hasWalletHere(): Promise<boolean> {
  if (readFlagSync().has) return true;
  return (await readFlagIdb()).has;
}

/** The cached wallet's address, if any — for pre-checks before any prompt. */
export function cachedAddress(): string | null {
  return readCredential()?.address ?? null;
}

/**
 * Connect the wallet that will act.
 *  - usePicker=false: silent rebuild from m.cred (no biometric); falls back to
 *    the login ceremony (one Face ID) and re-caches.
 *  - usePicker=true: force the system passkey picker ("another wallet");
 *    does NOT overwrite the cached default.
 */
export async function connectHere(
  adapter: WalletAdapter,
  usePicker: boolean,
): Promise<{ address: string }> {
  if (!usePicker) {
    const cached = readCredential();
    if (cached) {
      try {
        return await adapter.connectWithCredential(cached);
      } catch (e) {
        console.warn("[M] cached connect failed; using login", e);
        clearCredential();
      }
    }
  }
  const r = await adapter.createOrConnect("login");
  if (!usePicker) saveCredential({ ...r.credential, address: r.address });
  setFlag(null);
  return { address: r.address };
}

/**
 * v0.70.12 — "What shall we call you?" — asked BEFORE the passkey ceremony so
 * the passkey list can carry the person's own name ("M® · Lucy Parker"); it
 * is also saved as their profile name. Optional: Skip → "M® Wallet · CODE".
 */
// v0.70.19 — required: every wallet has a name (the "no codes" rule needs one).
// Returns a non-empty name; Continue stays disabled until there is one.
export function askDisplayName(root: HTMLElement, opts: { returning?: boolean; cta?: string; value?: string; note?: string; slot?: boolean } = {}): Promise<string> {
  if (opts.slot) return askNameInSlot(root, opts);
  return new Promise((resolve) => {
    root.innerHTML = `
      <main class="screen pay center lg21">
        <div class="brand">${LOGO_M}</div>
        <h1 class="serif21">What shall we call you?</h1>
        ${opts.returning ? `<p class="sub">One thing before we go on.</p>` : ""}
        <p class="sub ask21">Choose a name, or a nickname, you will use on M®. Shown to people you pay and who pay you. Never public, never searchable.</p>
        <p class="sub ask21-note muted21">You can change it any time in Settings.</p>
        <input id="askName" class="nick-input" type="text" placeholder="e.g. Lucy Parker" maxlength="40" autocomplete="name" autocapitalize="words" />
        <div class="actions">
          <button id="askGo" class="btn primary block" disabled>${escapeHtml(opts.cta ?? "Continue")}</button>
        </div>
        ${opts.note ? `<p id="askNote" class="status warn" role="status">${escapeHtml(opts.note)}</p>` : ""}
      </main>`;
    const input = root.querySelector<HTMLInputElement>("#askName")!;
    const go = root.querySelector<HTMLButtonElement>("#askGo")!;
    const clean = () => input.value.replace(/\s+/g, " ").trim();
    if (opts.value) { input.value = opts.value; go.disabled = !clean(); } // 1.4.39 — a retry keeps the name
    input.addEventListener("input", () => (go.disabled = !clean()));
    const done = () => {
      const v = clean();
      if (v) resolve(v);
    };
    go.addEventListener("click", done);
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") done();
    });
    setTimeout(() => input.focus(), 60);
  });
}

const IC_X_NAME = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>`;

/** app 1.4.70 (Alexander, 21 Oct) — the sign-up name screen as the take page asks the name: the question is the
 *  field's placeholder, the line above says what the name is for, and there is no button: the take page's box
 *  (its 16px rectangle, the muted ✕ springing in, the green → growing in) — → or Return makes the wallet. The field
 *  stands where the landing's pill and the code field stand (.slot69) and holds still (holdField). */
function askNameInSlot(root: HTMLElement, opts: { value?: string; note?: string }): Promise<string> {
  return new Promise((resolve) => {
    const v0 = opts.value ?? "";
    root.innerHTML = `
      <main class="screen welcome lg21 ask69 slot69-screen">
        <div class="brand">${LOGO_M}</div>
        <div class="slot69">
          <p class="line69 ask69-line above69">Choose a name you'll use on M®.</p>
          <div id="askAct" class="nt-act" data-mode="name">
            <input id="askName" class="nt-name" type="text" placeholder="What shall we call you?" maxlength="40" autocomplete="name" autocapitalize="words" autocorrect="off" spellcheck="false" enterkeyhint="go" value="${escapeHtml(v0)}" aria-label="What shall we call you?" />
            <button id="askClr" class="nt-clr" type="button" aria-label="Clear" tabindex="-1">${IC_X_NAME}</button>
            <button id="askGo" class="nt-arrow" type="button" aria-label="Create wallet"${v0.trim() ? "" : " disabled"}><span class="o">${ARROW_CIRCLE}</span><span class="f">${ARROW_CIRCLE_FILL}</span></button>
            <span class="nt-making" aria-hidden="true"><span class="spinner"></span>Creating your wallet…</span>
          </div>
          <div class="below69">${opts.note ? `<p id="askNote" class="status warn" role="status">${escapeHtml(opts.note)}</p>` : ""}</div>
        </div>
      </main>`;
    const input = root.querySelector<HTMLInputElement>("#askName")!;
    const go = root.querySelector<HTMLButtonElement>("#askGo")!;
    const act = root.querySelector<HTMLElement>("#askAct")!;
    const clean = () => input.value.replace(/\s+/g, " ").trim();
    // the take page's paintName: x + on while there is a name; xout plays the ✕ away once
    const paint = (first = false) => {
      const has = clean().length > 0;
      go.disabled = !has;
      if (first) act.classList.add("still");
      if (has) { act.classList.remove("xout"); act.classList.add("x", "on"); }
      else if (act.classList.contains("x")) { act.classList.remove("x", "on"); act.classList.add("xout"); }
      if (first) requestAnimationFrame(() => act.classList.remove("still"));
    };
    paint(true);
    input.addEventListener("input", () => paint());
    root.querySelector("#askClr")?.addEventListener("click", () => { input.value = ""; paint(); input.focus({ preventScroll: true }); });
    let sent = false;
    const done = () => {
      const v = clean();
      if (!v || sent) return;
      sent = true;
      resolve(v);
    };
    go.addEventListener("click", done);
    input.addEventListener("keydown", (e) => { if (e.key === "Enter") done(); });
    holdField(input, () => root.querySelector(".ask69-line"));
    // 1.4.73 (Alexander's video, 21 Oct) — not focused at load: a field the app focuses opens the keyboard by itself,
    // and iOS then scrolls the page to reveal it (twice in Chrome: the keyboard, then its AutoFill bar). The field
    // waits for a tap, as the take page's does, and the tap goes through holdStill: nothing moves.
  });
}

/** Create a wallet in this browser (register + name), as payView's create-and-pay does. */
export async function createWalletHere(adapter: WalletAdapter, displayName?: string | null): Promise<{ address: string }> {
  let label: string | undefined;
  let slug: string | undefined;
  let display: string | undefined;
  try {
    const p = await proposeName();
    label = p.slug;
    slug = p.slug;
    display = p.display;
  } catch {
    label = `m-wallet-${Date.now().toString(36)}`;
  }
  const conn = await adapter.createOrConnect("register", label, displayName);
  saveCredential({ ...conn.credential, address: conn.address });
  setFlag(display ?? null);
  // The claim needs a wallet code on the record — wait for it here (the home
  // path can afford to fire-and-forget; the claim page can't).
  if (slug) await assignName(conn.address, slug).catch(() => {});
  if (displayName) await setProfile({ address: conn.address, name: displayName }).catch(() => {});
  return { address: conn.address };
}

export function escapeHtml(s: string) {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c] as string,
  );
}
